import { supabase } from '@/integrations/supabase/client';

export interface FailureCode {
  id: string;
  code: string;
  label: string;
  category: string;
  active: boolean;
}

export interface ActionCode {
  id: string;
  code: string;
  label: string;
  active: boolean;
}

export async function fetchFailureCodes(orgId: string | null): Promise<FailureCode[]> {
  let q = supabase.from('failure_codes').select('id, code, label, category, active').order('code');
  if (orgId) q = q.eq('organization_id', orgId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as FailureCode[];
}

export async function fetchActionCodes(orgId: string | null): Promise<ActionCode[]> {
  let q = supabase.from('action_codes').select('id, code, label, active').order('code');
  if (orgId) q = q.eq('organization_id', orgId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as ActionCode[];
}

export async function createFailureCode(
  code: string,
  label: string,
  category: string,
  organizationId: string,
): Promise<void> {
  const { error } = await supabase
    .from('failure_codes')
    .insert({ organization_id: organizationId, code: code.trim().toUpperCase(), label: label.trim(), category: category.trim(), active: true });
  if (error) throw new Error(error.message);
}

export async function createActionCode(code: string, label: string, organizationId: string): Promise<void> {
  const { error } = await supabase
    .from('action_codes')
    .insert({ organization_id: organizationId, code: code.trim().toUpperCase(), label: label.trim(), active: true });
  if (error) throw new Error(error.message);
}

export async function toggleCodeActive(
  table: 'failure_codes' | 'action_codes' | 'root_cause_codes',
  id: string,
  active: boolean,
): Promise<void> {
  const { error } = await supabase.from(table).update({ active }).eq('id', id);
  if (error) throw new Error(error.message);
}

// ── Root Cause Codes ─────────────────────────────────────────────────────────

export interface RootCauseCode {
  id: string;
  code: string;
  label: string;
  category: string;
  active: boolean;
}

export const ROOT_CAUSE_CATEGORIES = [
  'wear',
  'material',
  'design',
  'process',
  'operator',
  'external',
] as const;

export type RootCauseCategory = (typeof ROOT_CAUSE_CATEGORIES)[number];

export const rootCauseCategoryLabels: Record<RootCauseCategory, string> = {
  wear: 'Wear & Fatigue',
  material: 'Material Issue',
  design: 'Mold Design',
  process: 'Process Drift',
  operator: 'Operator / Setup',
  external: 'External / Equipment',
};

export async function fetchRootCauseCodes(orgId: string | null): Promise<RootCauseCode[]> {
  let q = supabase.from('root_cause_codes').select('id, code, label, category, active').order('code');
  if (orgId) q = q.eq('organization_id', orgId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return (data ?? []) as RootCauseCode[];
}

export async function createRootCauseCode(
  code: string,
  label: string,
  category: string,
  organizationId: string,
): Promise<void> {
  const { error } = await supabase
    .from('root_cause_codes')
    .insert({ organization_id: organizationId, code: code.trim().toUpperCase(), label: label.trim(), category: category.trim(), active: true });
  if (error) throw new Error(error.message);
}

// ── Org Settings ──────────────────────────────────────────────────────────────

export interface OrgSettings {
  pm_due_soon_shots: number;
  pm_due_soon_days: number;
  chronic_window_days: number;
  chronic_min_failures: number;
  chronic_same_code_threshold: number;
  inactivity_timeout_minutes: number;
  auto_assign_to_creator: boolean;
  require_failure_code_at_close: boolean;
  require_action_code_at_close: boolean;
  technician_count: number;              // team size — used for blocked time capacity %
  labor_hourly_rate: number;
  chronic_critical_count: number;        // failures >= this → 'critical' status (default 5)
  chronic_critical_mttr_hours: number;   // avg MTTR >= this → 'critical' status (default 8)
  wo_aging_threshold_days: number;
  priority_override_order: string[];
  // Shift window — used to filter "Closed Today" by shift, not calendar day
  shift_start_hour: number;
  shift_end_hour: number;
  // Default estimated duration for CM/corrective WOs (minutes)
  default_cm_duration_minutes: number;
  // Optimistic locking version for priority override order
  priority_override_version: number;
  // ── Configurable targets (Phase 0A) ──────────────────────────────────────────
  mttr_target_hours: number;             // MTTR target for insights — default 8
  pm_compliance_target_pct: number;      // PM compliance target — default 85
  hours_per_tech: number;                // hours per technician per shift — default 8
  require_photo_for_impacts: string[];   // impacts that require photo at closure
  require_manager_signoff_for_impacts: string[]; // impacts that require manager approval before closing
  scrap_nc_threshold: number;            // pcs scrapped in a run before a draft NC is auto-created
  // ── Feature flags (per-org module toggles) ────────────────────────────────
  feature_spc: boolean;                  // SPC charts on quality dashboard
  feature_8d: boolean;                   // 8D workflow tab on NC detail
  feature_fai: boolean;                  // First Article Inspection gate on orders
  feature_labor_law: boolean;            // Labor law compliance panel on reports
  feature_saft_export: boolean;          // SAF-T PT export in settings
  require_signature_at_close: boolean;   // require signature capture to close WO
}

export const DEFAULT_ORG_SETTINGS: OrgSettings = {
  pm_due_soon_shots: 5000,
  pm_due_soon_days: 30,
  chronic_window_days: 90,
  chronic_min_failures: 3,
  chronic_same_code_threshold: 2,
  inactivity_timeout_minutes: 60,
  auto_assign_to_creator: false,
  require_failure_code_at_close: true,
  require_action_code_at_close: true,
  technician_count: 4,
  labor_hourly_rate: 60,
  chronic_critical_count: 5,
  chronic_critical_mttr_hours: 8,
  wo_aging_threshold_days: 3,
  priority_override_order: [],
  shift_start_hour: 6,
  shift_end_hour: 14,
  default_cm_duration_minutes: 120,
  priority_override_version: 0,
  mttr_target_hours: 8,
  pm_compliance_target_pct: 85,
  hours_per_tech: 8,
  require_photo_for_impacts: ['line_down', 'safety'],
  require_manager_signoff_for_impacts: [],
  scrap_nc_threshold: 10,
  feature_spc: true,
  feature_8d: true,
  feature_fai: true,
  feature_labor_law: true,
  feature_saft_export: true,
  require_signature_at_close: false,
};

export async function fetchOrgSettings(orgId: string): Promise<OrgSettings> {
  const { data, error } = await supabase
    .from('organizations')
    .select('settings')
    .eq('id', orgId)
    .single();
  if (error || !data) return DEFAULT_ORG_SETTINGS;
  return { ...DEFAULT_ORG_SETTINGS, ...(data.settings as Partial<OrgSettings>) };
}

export async function updateOrgSettings(orgId: string, patch: Partial<OrgSettings>): Promise<void> {
  // Merge patch into existing settings using jsonb || operator
  const { error } = await supabase.rpc('merge_org_settings', { org_id: orgId, patch: patch });
  if (error) {
    // Fallback: fetch current, merge, update
    const current = await fetchOrgSettings(orgId);
    const merged = { ...current, ...patch };
    const { error: err2 } = await supabase
      .from('organizations')
      .update({ settings: merged })
      .eq('id', orgId);
    if (err2) throw new Error(err2.message);
  }
}
